import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FilterIcon, DownloadIcon, EyeIcon, EditIcon, TrashIcon, CheckCircleIcon, XCircleIcon, ClockIcon, HistoryIcon, AlertCircleIcon, RefreshCwIcon, BuildingIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
// Enhanced merchant data with status history
const merchantsData = [{
  id: 1,
  name: 'ABC Corporation',
  businessType: 'Retail',
  status: 'active',
  region: 'North',
  onboardingDate: '2023-05-15',
  lastActivityDate: '2023-10-25',
  aggregator: null,
  contactPerson: 'John Smith',
  email: 'john@abccorp.com',
  phone: '+1 (555) 123-4567',
  statusHistory: [{
    previousStatus: 'pending',
    newStatus: 'active',
    changedBy: 'admin@example.com',
    changedAt: '2023-05-20T10:30:00Z',
    reason: 'All requirements met'
  }]
}, {
  id: 2,
  name: 'XYZ Enterprises',
  businessType: 'E-commerce',
  status: 'active',
  region: 'South',
  onboardingDate: '2023-06-22',
  lastActivityDate: '2023-10-28',
  aggregator: 'Global Payments',
  contactPerson: 'Jane Doe',
  email: 'jane@xyzent.com',
  phone: '+1 (555) 987-6543',
  statusHistory: [{
    previousStatus: 'pending',
    newStatus: 'active',
    changedBy: 'admin@example.com',
    changedAt: '2023-06-25T14:15:00Z',
    reason: 'Approved after document verification'
  }]
}, {
  id: 3,
  name: 'Local Grocers',
  businessType: 'Grocery',
  status: 'inactive',
  region: 'East',
  onboardingDate: '2023-04-10',
  lastActivityDate: '2023-09-15',
  aggregator: null,
  contactPerson: 'Mike Johnson',
  email: 'mike@localgrocers.com',
  phone: '+1 (555) 456-7890',
  statusHistory: [{
    previousStatus: 'active',
    newStatus: 'inactive',
    changedBy: 'admin@example.com',
    changedAt: '2023-09-15T09:45:00Z',
    reason: 'Business closed operations'
  }]
}, {
  id: 4,
  name: 'Tech Solutions Inc',
  businessType: 'Technology',
  status: 'pending',
  region: 'West',
  onboardingDate: '2023-07-05',
  lastActivityDate: '2023-10-20',
  aggregator: 'Innovate Partners',
  contactPerson: 'Sarah Williams',
  email: 'sarah@techsolutions.com',
  phone: '+1 (555) 234-5678',
  statusHistory: [{
    previousStatus: null,
    newStatus: 'pending',
    changedBy: 'system',
    changedAt: '2023-07-05T11:20:00Z',
    reason: 'Initial application submitted'
  }]
}, {
  id: 5,
  name: 'Metro Retailers',
  businessType: 'Retail',
  status: 'suspended',
  region: 'Central',
  onboardingDate: '2023-03-18',
  lastActivityDate: '2023-10-10',
  aggregator: null,
  contactPerson: 'Robert Brown',
  email: 'robert@metroretailers.com',
  phone: '+1 (555) 876-5432',
  statusHistory: [{
    previousStatus: 'active',
    newStatus: 'suspended',
    changedBy: 'compliance@example.com',
    changedAt: '2023-10-10T16:30:00Z',
    reason: 'Compliance review required'
  }, {
    previousStatus: 'pending',
    newStatus: 'active',
    changedBy: 'admin@example.com',
    changedAt: '2023-04-02T13:15:00Z',
    reason: 'All requirements met'
  }]
}];
interface StatusHistoryItem {
  previousStatus: string | null;
  newStatus: string;
  changedBy: string;
  changedAt: string;
  reason: string;
}
interface Merchant {
  id: number;
  name: string;
  businessType: string;
  status: string;
  region: string;
  onboardingDate: string;
  lastActivityDate: string;
  aggregator: string | null;
  contactPerson: string;
  email: string;
  phone: string;
  statusHistory: StatusHistoryItem[];
}
type MerchantStatus = 'active' | 'inactive' | 'pending' | 'suspended' | string;
const MerchantList: React.FC = () => {
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState<Merchant[]>(merchantsData);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showStatusHistoryModal, setShowStatusHistoryModal] = useState(false);
  const [newStatus, setNewStatus] = useState<MerchantStatus>('');
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const handleAddMerchant = () => {
    navigate('/merchants/new');
  };
  const handleEditMerchant = (merchant: Merchant) => {
    navigate(`/merchants/edit/${merchant.id}`);
  };
  const handleDeleteMerchant = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setShowDeleteModal(true);
  };
  const handleViewMerchant = (merchant: Merchant) => {
    navigate(`/merchants/details/${merchant.id}`);
  };
  const handleChangeStatus = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setNewStatus(merchant.status);
    setShowStatusModal(true);
  };
  const handleViewStatusHistory = (merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setShowStatusHistoryModal(true);
  };
  const confirmDelete = () => {
    // Delete logic would go here
    setShowDeleteModal(false);
    setSelectedMerchant(null);
  };
  const confirmStatusChange = () => {
    if (!selectedMerchant || !newStatus || newStatus === selectedMerchant.status) {
      setShowStatusModal(false);
      return;
    }
    // Update merchant status and add to history
    const updatedMerchants = merchants.map(merchant => {
      if (merchant.id === selectedMerchant.id) {
        const statusHistoryItem: StatusHistoryItem = {
          previousStatus: merchant.status,
          newStatus: newStatus,
          changedBy: 'current.user@example.com',
          changedAt: new Date().toISOString(),
          reason: statusChangeReason
        };
        return {
          ...merchant,
          status: newStatus,
          statusHistory: [statusHistoryItem, ...merchant.statusHistory]
        };
      }
      return merchant;
    });
    setMerchants(updatedMerchants);
    setShowStatusModal(false);
    setStatusChangeReason('');
    setNewStatus('');
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-4709" />;
      case 'inactive':
        return <StatusBadge status="danger" text="Inactive" data-id="element-4710" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending" data-id="element-4711" />;
      case 'suspended':
        return <StatusBadge status="info" text="Suspended" data-id="element-4712" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-4713" />;
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-4714" />;
      case 'inactive':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-4715" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-4716" />;
      case 'suspended':
        return <AlertCircleIcon className="h-5 w-5 text-blue-500" data-id="element-4717" />;
      default:
        return null;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };
  const filteredMerchants = merchants.filter(merchant => statusFilter ? merchant.status === statusFilter : true);
  const columns = [{
    header: 'Merchant Name',
    accessor: 'name',
    sortable: true
  }, {
    header: 'Business Type',
    accessor: 'businessType',
    sortable: true
  }, {
    header: 'Status',
    accessor: (merchant: Merchant) => getStatusBadge(merchant.status),
    sortable: false
  }, {
    header: 'Region',
    accessor: 'region',
    sortable: true
  }, {
    header: 'Onboarding Date',
    accessor: (merchant: Merchant) => formatDate(merchant.onboardingDate),
    sortable: true
  }, {
    header: 'Last Activity',
    accessor: (merchant: Merchant) => formatDate(merchant.lastActivityDate),
    sortable: true
  }, {
    header: 'Aggregator',
    accessor: (merchant: Merchant) => merchant.aggregator || 'N/A',
    sortable: true
  }];
  return <div className="space-y-6" data-id="element-4718">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-4719">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4720">
          Merchant Management
        </h1>
        <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-4721">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4722">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-4723" />
            Filters
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4724">
            <DownloadIcon className="h-4 w-4 mr-2" data-id="element-4725" />
            Export
          </button>
          <button onClick={handleAddMerchant} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-4726">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-4727" />
            Add Merchant
          </button>
        </div>
      </div>

      {showFilters && <Card data-id="element-4728">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4729">
            <div data-id="element-4730">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4731">
                Status
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-4732">
                <option value="" data-id="element-4733">All Statuses</option>
                <option value="active" data-id="element-4734">Active</option>
                <option value="inactive" data-id="element-4735">Inactive</option>
                <option value="pending" data-id="element-4736">Pending</option>
                <option value="suspended" data-id="element-4737">Suspended</option>
              </select>
            </div>
            <div data-id="element-4738">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4739">
                Region
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-4740">
                <option value="" data-id="element-4741">All Regions</option>
                <option value="North" data-id="element-4742">North</option>
                <option value="South" data-id="element-4743">South</option>
                <option value="East" data-id="element-4744">East</option>
                <option value="West" data-id="element-4745">West</option>
                <option value="Central" data-id="element-4746">Central</option>
              </select>
            </div>
            <div data-id="element-4747">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4748">
                Aggregator
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-4749">
                <option value="" data-id="element-4750">All Aggregators</option>
                <option value="Global Payments" data-id="element-4751">Global Payments</option>
                <option value="Innovate Partners" data-id="element-4752">Innovate Partners</option>
                <option value="none" data-id="element-4753">Direct Merchants</option>
              </select>
            </div>
            <div data-id="element-4754">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4755">
                Business Type
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-4756">
                <option value="" data-id="element-4757">All Types</option>
                <option value="Retail" data-id="element-4758">Retail</option>
                <option value="E-commerce" data-id="element-4759">E-commerce</option>
                <option value="Grocery" data-id="element-4760">Grocery</option>
                <option value="Technology" data-id="element-4761">Technology</option>
              </select>
            </div>
            <div data-id="element-4762">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4763">
                Onboarding Date From
              </label>
              <input type="date" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-4764" />
            </div>
            <div data-id="element-4765">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-4766">
                Onboarding Date To
              </label>
              <input type="date" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-4767" />
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-4768">
            <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => setStatusFilter('')} data-id="element-4769">
              Reset
            </button>
            <button className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-4770">
              Apply Filters
            </button>
          </div>
        </Card>}

      <Card data-id="element-4771">
        <div className="mb-4 flex items-center justify-between" data-id="element-4772">
          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4773">
            {statusFilter ? `Showing ${filteredMerchants.length} ${statusFilter} merchants` : `Showing all ${merchants.length} merchants`}
          </div>
          <div className="flex items-center space-x-2" data-id="element-4774">
            <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4775">
              Bulk Actions:
            </span>
            <select className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-1 px-2 text-sm" data-id="element-4776">
              <option value="" data-id="element-4777">Select Action</option>
              <option value="active" data-id="element-4778">Set Active</option>
              <option value="inactive" data-id="element-4779">Set Inactive</option>
              <option value="suspended" data-id="element-4780">Set Suspended</option>
            </select>
            <button className="px-3 py-1 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-4781">
              Apply
            </button>
          </div>
        </div>
        <Table columns={columns} data={filteredMerchants} keyField="id" actions={merchant => <div className="flex justify-end space-x-2" data-id="element-4783">
              <button onClick={() => handleViewMerchant(merchant)} className="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="View" data-id="element-4784">
                <EyeIcon className="h-5 w-5" data-id="element-4785" />
              </button>
              <button onClick={() => handleEditMerchant(merchant)} className="p-1 rounded-full text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30" title="Edit" data-id="element-4786">
                <EditIcon className="h-5 w-5" data-id="element-4787" />
              </button>
              <button onClick={() => handleChangeStatus(merchant)} className="p-1 rounded-full text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30" title="Change Status" data-id="element-4788">
                <RefreshCwIcon className="h-5 w-5" data-id="element-4789" />
              </button>
              <button onClick={() => handleDeleteMerchant(merchant)} className="p-1 rounded-full text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="Delete" data-id="element-4790">
                <TrashIcon className="h-5 w-5" data-id="element-4791" />
              </button>
            </div>} data-id="element-4782" />
      </Card>

      {/* View Merchant Modal */}
      <Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)} title="Merchant Details" size="lg" data-id="element-4792">
        {selectedMerchant && <div className="space-y-4" data-id="element-4793">
            <div className="flex items-center" data-id="element-4794">
              <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-3" data-id="element-4795">
                {getStatusIcon(selectedMerchant.status)}
              </div>
              <div data-id="element-4796">
                <h3 className="text-lg font-medium" data-id="element-4797">{selectedMerchant.name}</h3>
                <div className="flex items-center mt-1" data-id="element-4798">
                  {getStatusBadge(selectedMerchant.status)}
                  <span className="mx-2 text-gray-500 dark:text-gray-400" data-id="element-4799">
                    •
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4800">
                    {selectedMerchant.businessType}
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-4801">
              <div data-id="element-4802">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4803">
                  Contact Information
                </h4>
                <div className="mt-2 space-y-2" data-id="element-4804">
                  <p data-id="element-4805">
                    <span className="font-medium" data-id="element-4806">Contact Person:</span>{' '}
                    {selectedMerchant.contactPerson}
                  </p>
                  <p data-id="element-4807">
                    <span className="font-medium" data-id="element-4808">Email:</span>{' '}
                    {selectedMerchant.email}
                  </p>
                  <p data-id="element-4809">
                    <span className="font-medium" data-id="element-4810">Phone:</span>{' '}
                    {selectedMerchant.phone}
                  </p>
                </div>
              </div>
              <div data-id="element-4811">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4812">
                  Merchant Details
                </h4>
                <div className="mt-2 space-y-2" data-id="element-4813">
                  <p data-id="element-4814">
                    <span className="font-medium" data-id="element-4815">Region:</span>{' '}
                    {selectedMerchant.region}
                  </p>
                  <p data-id="element-4816">
                    <span className="font-medium" data-id="element-4817">Onboarding Date:</span>{' '}
                    {formatDate(selectedMerchant.onboardingDate)}
                  </p>
                  <p data-id="element-4818">
                    <span className="font-medium" data-id="element-4819">Last Activity:</span>{' '}
                    {formatDate(selectedMerchant.lastActivityDate)}
                  </p>
                  <p data-id="element-4820">
                    <span className="font-medium" data-id="element-4821">Aggregator:</span>{' '}
                    {selectedMerchant.aggregator || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-4822">
              <div className="flex justify-between items-center mb-2" data-id="element-4823">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-4824">
                  Status History
                </h4>
                <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" onClick={() => {
              setShowViewModal(false);
              handleViewStatusHistory(selectedMerchant);
            }} data-id="element-4825">
                  <HistoryIcon className="h-4 w-4 mr-1" data-id="element-4826" />
                  View Full History
                </button>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto" data-id="element-4827">
                {selectedMerchant.statusHistory.slice(0, 2).map((history, index) => <div key={index} className="bg-gray-50 dark:bg-gray-800 p-2 rounded-md text-sm" data-id="element-4828">
                      <div className="flex justify-between" data-id="element-4829">
                        <div data-id="element-4830">
                          <span className="font-medium" data-id="element-4831">
                            {history.previousStatus ? `${history.previousStatus.toUpperCase()} → ${history.newStatus.toUpperCase()}` : `Initial status: ${history.newStatus.toUpperCase()}`}
                          </span>
                        </div>
                        <div className="text-gray-500 dark:text-gray-400" data-id="element-4832">
                          {formatDateTime(history.changedAt)}
                        </div>
                      </div>
                      <div className="mt-1 text-gray-600 dark:text-gray-300" data-id="element-4833">
                        {history.reason}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-4834">
                        Changed by: {history.changedBy}
                      </div>
                    </div>)}
                {selectedMerchant.statusHistory.length > 2 && <div className="text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-4835">
                    {selectedMerchant.statusHistory.length - 2} more entries...
                  </div>}
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-4836">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-4837">
                Quick Actions
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-4838">
                <button className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-md text-sm flex items-center" onClick={() => {
              setShowViewModal(false);
              handleChangeStatus(selectedMerchant);
            }} data-id="element-4839">
                  <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-4840" />
                  Change Status
                </button>
                <button className="px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-sm" data-id="element-4841">
                  View Transactions
                </button>
                <button className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-md text-sm" data-id="element-4842">
                  View Tills
                </button>
                <button className="px-3 py-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 rounded-md text-sm" data-id="element-4843">
                  Generate QR
                </button>
              </div>
            </div>
          </div>}
      </Modal>

      {/* Change Status Modal */}
      <Modal isOpen={showStatusModal} onClose={() => setShowStatusModal(false)} title="Change Merchant Status" footer={<>
            <button onClick={() => setShowStatusModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4845">
              Cancel
            </button>
            <button onClick={confirmStatusChange} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" disabled={!newStatus || selectedMerchant && newStatus === selectedMerchant.status} data-id="element-4846">
              Update Status
            </button>
          </>} data-id="element-4844">
        {selectedMerchant && <div className="space-y-4" data-id="element-4847">
            <div className="flex items-center mb-4" data-id="element-4848">
              <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-3" data-id="element-4849">
                <BuildingIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-4850" />
              </div>
              <div data-id="element-4851">
                <h3 className="text-lg font-medium" data-id="element-4852">{selectedMerchant.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4853">
                  Current Status: {getStatusBadge(selectedMerchant.status)}
                </p>
              </div>
            </div>
            <div data-id="element-4854">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-4855">
                New Status
              </label>
              <select value={newStatus} onChange={e => setNewStatus(e.target.value as MerchantStatus)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-4856">
                <option value="" data-id="element-4857">Select Status</option>
                <option value="active" data-id="element-4858">Active</option>
                <option value="inactive" data-id="element-4859">Inactive</option>
                <option value="pending" data-id="element-4860">Pending</option>
                <option value="suspended" data-id="element-4861">Suspended</option>
              </select>
            </div>
            <div data-id="element-4862">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-4863">
                Reason for Status Change
              </label>
              <textarea value={statusChangeReason} onChange={e => setStatusChangeReason(e.target.value)} rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter reason for status change" required data-id="element-4864" />
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded p-3" data-id="element-4865">
              <p className="text-sm text-yellow-800 dark:text-yellow-300 flex items-start" data-id="element-4866">
                <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-4867" />
                <span data-id="element-4868">
                  <strong data-id="element-4869">Note:</strong> Changing a merchant's status may affect
                  their ability to process transactions. This action will be
                  recorded in the audit log.
                </span>
              </p>
            </div>
          </div>}
      </Modal>

      {/* Status History Modal */}
      <Modal isOpen={showStatusHistoryModal} onClose={() => setShowStatusHistoryModal(false)} title="Status Change History" size="lg" data-id="element-4870">
        {selectedMerchant && <div className="space-y-4" data-id="element-4871">
            <div className="flex items-center mb-4" data-id="element-4872">
              <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-3" data-id="element-4873">
                <BuildingIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-4874" />
              </div>
              <div data-id="element-4875">
                <h3 className="text-lg font-medium" data-id="element-4876">{selectedMerchant.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4877">
                  Current Status: {getStatusBadge(selectedMerchant.status)}
                </p>
              </div>
            </div>
            <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mb-4" data-id="element-4878">
              <h4 className="font-medium text-gray-700 dark:text-gray-300" data-id="element-4879">
                Complete Status History
              </h4>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto" data-id="element-4880">
              {selectedMerchant.statusHistory.map((history, index) => <div key={index} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md" data-id="element-4881">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center" data-id="element-4882">
                    <div className="flex items-center mb-2 sm:mb-0" data-id="element-4883">
                      {history.previousStatus ? <div className="flex items-center" data-id="element-4884">
                          <span className="inline-block" data-id="element-4885">
                            {getStatusBadge(history.previousStatus)}
                          </span>
                          <ArrowRightIcon className="h-4 w-4 mx-2 text-gray-500 dark:text-gray-400" data-id="element-4886" />
                          <span className="inline-block" data-id="element-4887">
                            {getStatusBadge(history.newStatus)}
                          </span>
                        </div> : <div className="flex items-center" data-id="element-4888">
                          <span className="text-sm text-gray-500 dark:text-gray-400 mr-2" data-id="element-4889">
                            Initial status:
                          </span>
                          <span className="inline-block" data-id="element-4890">
                            {getStatusBadge(history.newStatus)}
                          </span>
                        </div>}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4891">
                      {formatDateTime(history.changedAt)}
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-4892">
                    <span className="font-medium" data-id="element-4893">Reason:</span>{' '}
                    {history.reason}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-4894">
                    Changed by: {history.changedBy}
                  </div>
                </div>)}
              {selectedMerchant.statusHistory.length === 0 && <div className="text-center py-4 text-gray-500 dark:text-gray-400" data-id="element-4895">
                  No status change history available.
                </div>}
            </div>
          </div>}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Deletion" footer={<>
            <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-4897">
              Cancel
            </button>
            <button onClick={confirmDelete} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-4898">
              Delete
            </button>
          </>} data-id="element-4896">
        <p className="text-gray-700 dark:text-gray-300" data-id="element-4899">
          Are you sure you want to delete the merchant "{selectedMerchant?.name}
          "? This action cannot be undone.
        </p>
      </Modal>
    </div>;
};
export default MerchantList;